import { useEffect, useMemo, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  adminApi,
  ApiError,
  type BrandingProblem,
  type BrandingValues,
  type Application,
} from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { Select } from '../components/Select.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  Field,
  OutcomeNote,
  Panel,
  PanelBody,
  inputStyle,
} from '../components/ui.js';

/**
 * The login-branding editor. RidgeWill/org-admin#86, over RidgeWill/auth#436
 * and its follow-ups.
 *
 * ── WHY THIS SCREEN EXISTS AT ALL ────────────────────────────────────
 *
 * The serving path DROPS a value it cannot use, silently, and renders corporate
 * instead. That is right for a login page — it must render — and it is useless
 * to a person typing a value: they see no error, no change, and conclude the
 * feature is broken. auth#441 is that exact experience, and it was a real bug
 * hiding behind the silence.
 *
 * So this screen's job is not "expose twenty inputs". It is to make the two
 * things an admin cannot otherwise see visible: what the login page would
 * ACTUALLY render, and why anything they typed was refused.
 *
 * ── THE SERVER IS THE ONLY JUDGE ─────────────────────────────────────
 *
 * Nothing here validates a colour, measures contrast, or decides what is
 * brandable. All three live in `services/auth/src/lib/client-branding.ts`, and
 * a copy in this bundle would drift the first time a rule changed — producing a
 * form that accepts what the page then discards, which is the same silence
 * wearing a different hat.
 *
 * `/check` is therefore called on every edit, debounced. It costs a request per
 * pause in typing and buys an editor that can never disagree with the thing it
 * is editing.
 */

/**
 * The fields, grouped the way somebody thinks about them rather than the way
 * the table stores them.
 *
 * `surface` is one group DELIBERATELY, and its heading says so: the server
 * refuses those nine together, so presenting them as nine independent inputs
 * would make a single rejection look like eight fields mysteriously reverting.
 */
const GROUPS: {
  title: string;
  hint?: string;
  fields: { key: string; label: string; hint?: string; choice?: true }[];
}[] = [
  {
    title: 'Identity',
    fields: [
      { key: 'wordmark', label: 'Wordmark', hint: 'The name beside the mark. Blank shows “Ridgewill”.' },
      {
        key: 'logoUrl',
        label: 'Mark — dark panel',
        hint: 'Absolute https URL. Drawn for the dark desktop panel.',
      },
      {
        key: 'markUrl',
        label: 'Mark — light surface',
        hint: 'Absolute https URL. What the mobile layout shows, on the light form background.',
      },
    ],
  },
  {
    title: 'Copy',
    fields: [
      { key: 'byline', label: 'Byline', hint: 'The panel headline. Up to 80 characters.' },
      { key: 'bylineDetail', label: 'Byline detail', hint: 'The line under it. Up to 160 characters.' },
      { key: 'eyebrow', label: 'Eyebrow', hint: 'The small line at the foot of the panel.' },
      { key: 'fontFamily', label: 'Font stack', hint: 'Panel only. The form keeps Public Sans.' },
    ],
  },
  {
    title: 'Accent and panel',
    hint: 'The accent themes every button, link and focus ring. The panel background is the one field where a gradient is allowed.',
    fields: [
      { key: 'accent', label: 'Accent' },
      { key: 'accentHover', label: 'Accent — hover' },
      { key: 'panelBackground', label: 'Panel background', hint: 'A colour or a gradient.' },
      { key: 'panelForeground', label: 'Panel text' },
      /**
       * The big faded ridge in the panel's corner (Jon, 2026-10-18): fine on a
       * solid panel, distracting over Ridgewill's sky photograph. A choice
       * rather than a box to type in, and Automatic is the blank that every
       * other field here has: the server decides it (auth's `watermarkFor`).
       */
      {
        key: 'panelWatermark',
        label: 'Faded mark on the sign-in panel',
        hint: 'Automatic shows it on your own panel background when you have no logo there, and never on Ridgewill’s sky photo.',
        choice: true,
      },
    ],
  },
  {
    title: 'Form surface',
    hint: 'These nine are stored and refused TOGETHER. If any text would be unreadable on the background it sits on, the whole group falls back to Ridgewill — so half a theme can never render.',
    fields: [
      { key: 'pageBackground', label: 'Page background', hint: 'Solid colours only, so contrast can be measured.' },
      { key: 'surface', label: 'Surface', hint: 'Inputs and buttons. Solid colours only.' },
      { key: 'textColor', label: 'Text' },
      { key: 'textMid', label: 'Text — secondary' },
      { key: 'muted', label: 'Text — hints' },
      { key: 'borderColor', label: 'Border' },
      { key: 'inputBorder', label: 'Input border' },
      { key: 'dangerText', label: 'Error text', hint: 'How a wrong password is shown. Held to the strictest contrast floor.' },
      { key: 'successText', label: 'Success text' },
    ],
  },
];

export const ALL_FIELDS = GROUPS.flatMap((g) => g.fields.map((f) => f.key));

/** The faded-mark choice, as the form holds it: '' is Automatic. */
const WATERMARK_CHOICES = [
  { value: '', label: 'Automatic' },
  { value: 'show', label: 'Show' },
  { value: 'hide', label: 'Hide' },
] as const;

/** Blank input means null, which the server reads as "back to corporate" —
 *  not as an empty string, which is not a colour and would be refused. The
 *  faded-mark choice goes as a boolean, and Automatic as null. */
export function toValues(form: Record<string, string>): BrandingValues {
  const out: BrandingValues = {};
  for (const key of ALL_FIELDS) out[key] = form[key]?.trim() ? form[key]!.trim() : null;
  out.panelWatermark = form.panelWatermark === 'show' ? true : form.panelWatermark === 'hide' ? false : null;
  return out;
}

export function toForm(values: BrandingValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of ALL_FIELDS) {
    const value = values[key];
    out[key] = typeof value === 'string' ? value : '';
  }
  out.panelWatermark = values.panelWatermark === true ? 'show' : values.panelWatermark === false ? 'hide' : '';
  return out;
}

export function ApplicationBranding({ app }: { app: Application }) {
  const slug = app.slug;
  const { state, reload } = useAsync(() => adminApi.clientBranding(slug), [slug]);

  return (
    <AsyncBoundary state={state}>
      {(loaded) => <Editor slug={slug} initial={loaded.stored} onSaved={reload} />}
    </AsyncBoundary>
  );
}

function Editor({
  slug,
  initial,
  onSaved,
}: {
  slug: string;
  initial: BrandingValues;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(() => toForm(initial));
  const [problems, setProblems] = useState<BrandingProblem[]>([]);
  const [effective, setEffective] = useState<BrandingValues>(initial);
  const action = useAction();

  const values = useMemo(() => toValues(form), [form]);

  /**
   * Ask the server what this would do, on a pause in typing.
   *
   * Debounced rather than on blur: the surface group's verdict depends on two
   * fields at once, so an admin adjusting a background wants the contrast
   * warning to move as they type rather than after they tab away from a field
   * that was never the problem.
   */
  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      void adminApi
        .checkClientBranding(slug, values)
        .then((res) => {
          if (!live) return;
          setProblems(res.problems);
          setEffective(res.effective);
        })
        .catch(() => {
          // A failed check must not block editing or invent a verdict. Save
          // still validates server-side, so the worst case is being told at
          // save time instead of before it.
          if (live) setProblems([]);
        });
    }, 350);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [slug, values]);

  const problemFor = (key: string) => problems.find((p) => p.field === key);
  const surfaceProblem = problems.find((p) => p.field === 'surface');

  return (
    <>
      <Panel title="Login branding">
        <PanelBody>
          {/* Says what the reader is looking at before they change anything:
              this is not a preference, it is what a signed-out person sees. */}
          <p style={{ fontSize: '.85rem', color: C.textMid, margin: '0 0 14px', maxWidth: 660 }}>
            What somebody signing in to <strong>{slug}</strong> sees on the hosted login page. Every
            field is optional, and a blank one means Ridgewill&rsquo;s own value rather than nothing.
          </p>

          <Preview values={effective} />

          {surfaceProblem ? (
            <div
              style={{
                border: `1px solid ${C.dangerBorder}`,
                background: C.dangerBg,
                color: C.dangerText,
                borderRadius: 8,
                padding: '10px 12px',
                fontSize: '.85rem',
                margin: '14px 0 0',
              }}
            >
              {surfaceProblem.message}
            </div>
          ) : null}

          {GROUPS.map((group) => (
            <section key={group.title} style={{ marginTop: 22 }}>
              <h3 style={{ fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: C.textMid2, margin: '0 0 4px' }}>
                {group.title}
              </h3>
              {group.hint ? (
                <p style={{ fontSize: '.82rem', color: C.textMid, margin: '0 0 12px', maxWidth: 640 }}>{group.hint}</p>
              ) : null}
              {group.fields.map((field) => {
                const problem = problemFor(field.key);
                if (field.choice) {
                  return (
                    <Field key={field.key} label={field.label} hint={problem ? undefined : field.hint} error={problem?.message}>
                      <Select
                        label={field.label}
                        value={(form[field.key] ?? '') as (typeof WATERMARK_CHOICES)[number]['value']}
                        options={WATERMARK_CHOICES}
                        onChange={(next) => setForm((f) => ({ ...f, [field.key]: next }))}
                      />
                    </Field>
                  );
                }
                return (
                  <Field
                    key={field.key}
                    label={field.label}
                    hint={problem ? undefined : field.hint}
                    error={problem?.message}
                  >
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        value={form[field.key] ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                        placeholder="Ridgewill default"
                        style={{
                          ...inputStyle,
                          ...(problem ? { borderColor: C.dangerBorder } : {}),
                        }}
                      />
                      <Swatch value={form[field.key] ?? ''} />
                    </div>
                  </Field>
                );
              })}
            </section>
          ))}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 22 }}>
            <Button
              disabled={action.running || problems.length > 0}
              title={problems.length > 0 ? 'Fix the problems above first.' : undefined}
              onClick={() =>
                void action
                  .run(async () => {
                    try {
                      await adminApi.setClientBranding(slug, values);
                    } catch (err) {
                      /**
                       * The refusal carries a problem per field. Surfacing them
                       * on the fields is the entire reason this endpoint
                       * refuses instead of silently dropping, so an error
                       * banner alone would throw away the thing that was
                       * expensive to build.
                       */
                      if (err instanceof ApiError && Array.isArray(err.detail?.problems)) {
                        setProblems(err.detail.problems as BrandingProblem[]);
                      }
                      throw err;
                    }
                    return 'Saved. The hosted login shows this on the next sign-in.';
                  })
                  .then(onSaved)
              }
            >
              {action.running ? 'Saving…' : 'Save'}
            </Button>
            <Button
              disabled={action.running}
              onClick={() => {
                setForm(toForm(initial));
                setProblems([]);
              }}
            >
              Revert
            </Button>
            <OutcomeNote outcome={action.outcome} />
          </div>
        </PanelBody>
      </Panel>
    </>
  );
}

/** A colour chip beside each input, so a hex value is something you can see
 *  rather than something you have to imagine. Silent for non-colours. */
function Swatch({ value }: { value: string }) {
  const looksLikeColour = /^(#|rgb|hsl|[a-z]+$)/i.test(value.trim());
  if (!value.trim() || !looksLikeColour) return <span style={{ width: 26 }} />;
  return (
    <span
      aria-hidden
      style={{
        width: 26,
        height: 26,
        flex: '0 0 26px',
        borderRadius: 6,
        border: `1px solid ${C.border}`,
        background: value,
      }}
    />
  );
}

/**
 * What the login page would render, from the server's `effective` — never from
 * the form.
 *
 * That distinction is the point. A preview built from the inputs would show an
 * admin their refused gradient looking fine, which is precisely the false
 * reassurance this screen exists to remove. What is drawn here has been through
 * the same function the login page uses.
 *
 * THE LITERAL HEXES BELOW ARE DELIBERATE — DO NOT 'TIDY' THEM INTO `C.*`.
 * Every fallback in here is the LOGIN PAGE's corporate default, not this
 * portal's chrome. Since the portal gained a dark theme, a `C.*` token would
 * repaint this preview in whatever theme the ADMIN is using and show them a
 * login page that does not exist -- a dark Keyodo sign-in that renders light
 * for every real user. The surrounding chrome (the heading, the caption, the
 * frame) does use tokens, correctly: that part is the portal.
 */
function Preview({ values }: { values: BrandingValues }) {
  const v = (key: string, fallback: string) => {
    const value = values[key];
    return typeof value === 'string' ? value : fallback;
  };
  const panelFg = v('panelForeground', '#ffffff');

  return (
    <div>
      <h3 style={{ fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: C.textMid2, margin: '0 0 4px' }}>
        Preview
      </h3>
      <p style={{ fontSize: '.82rem', color: C.textMid, margin: '0 0 10px' }}>
        Rendered from what the login page would actually use, not from the boxes below. A value the
        server refuses will not appear here.
      </p>
      <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', minHeight: 190 }}>
        <div
          style={{
            width: '44%',
            padding: 18,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            /* Ridgewill's panel is the site's sky photograph under a cobalt
               wash since auth#638, so that is the fallback: the faded mark's
               automatic setting turns on exactly that difference. */
            background: v('panelBackground', CORPORATE_PANEL),
            color: panelFg,
            fontFamily: typeof values.fontFamily === 'string' ? values.fontFamily : undefined,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 30 }}>
            {typeof values.logoUrl === 'string' ? (
              <img src={values.logoUrl} alt="" style={{ height: 26, width: 'auto' }} />
            ) : null}
            <strong style={{ fontSize: '1.15rem' }}>{v('wordmark', 'Ridgewill')}</strong>
          </div>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.15 }}>
              {v('byline', 'One account, every Ridgewill app.')}
            </div>
            <div style={{ fontSize: '.76rem', opacity: 0.72, marginTop: 5 }}>
              {v('bylineDetail', 'Sign in once. Your access, your agreements and your devices stay in one place.')}
            </div>
            <div style={{ fontSize: '.62rem', letterSpacing: '.12em', opacity: 0.58, marginTop: 12 }}>
              {v('eyebrow', 'ACCOUNTS · ACCESS · AGREEMENTS')}
            </div>
          </div>
          {/* The login page's corner mark, at the preview's scale, drawn only
              when the server's `effective` says the page draws it. */}
          {values.panelWatermark === true ? (
            <svg
              aria-hidden="true"
              width={110}
              height={110}
              viewBox="0 0 32 32"
              style={{ position: 'absolute', right: -20, bottom: -20, opacity: 0.12 }}
            >
              <path d="M3 26 L11 11 L19 26 Z" fill="#40E0D0" />
              <path d="M13 26 L22 8 L31 26 Z" fill={panelFg} />
            </svg>
          ) : null}
        </div>
        <div style={{ flex: 1, padding: 18, background: v('pageBackground', '#F6F8FB') }}>
          <div style={{ color: v('textColor', '#101626'), fontWeight: 700, fontSize: '1rem' }}>Sign in</div>
          <div style={{ color: v('textMid', '#3A4456'), fontSize: '.76rem', margin: '8px 0 3px' }}>Email</div>
          <div
            style={{
              background: v('surface', '#FFFFFF'),
              border: `1px solid ${v('inputBorder', '#D9DFE8')}`,
              borderRadius: 6,
              height: 26,
            }}
          />
          <div
            style={{
              background: v('accent', '#2B63E8'),
              color: '#fff',
              textAlign: 'center',
              borderRadius: 6,
              padding: '6px 0',
              marginTop: 8,
              fontSize: '.78rem',
              fontWeight: 600,
            }}
          >
            Continue
          </div>
          {/* The error line is previewed on purpose: it is the one thing an
              admin cannot see by signing in successfully, and the whole reason
              the contrast floor on `dangerText` exists. */}
          <div style={{ color: v('dangerText', '#B42318'), fontSize: '.72rem', marginTop: 8 }}>
            That password is not right.
          </div>
          <div style={{ color: v('muted', '#9AA4B2'), fontSize: '.68rem', marginTop: 6 }}>
            Hint text sits at this weight.
          </div>
        </div>
      </div>
    </div>
  );
}

/** Ridgewill's own sign-in panel (auth#638): the site's midday sky under the
 *  same cobalt wash and foot shade the login page draws. */
const CORPORATE_PANEL =
  'linear-gradient(0deg, rgba(10,16,30,.6) 0%, rgba(10,16,30,0) 45%), ' +
  'linear-gradient(160deg, rgba(18,48,127,.88) 0%, rgba(30,79,208,.55) 55%, rgba(30,79,208,.15) 100%), ' +
  '#26365a url(https://ridgewill.com/assets/home/sky-midday.webp) center / cover no-repeat';

/** Shown on the tab strip so an admin can see, without opening it, whether an
 *  app is branded at all. */
export function BrandingBadge({ configured }: { configured: boolean }) {
  return configured ? <Badge tone="good">branded</Badge> : <Badge>Ridgewill</Badge>;
}
